import { FC, useState } from 'react';
import { Loader, Maps, Svg, Switch, Text } from '@hopskipdrive/react-components';
import { Backdrop } from '@mui/material';
import { OverlayView, OverlayViewProps } from '@react-google-maps/api';

import { PhotoOrInitials } from '../../../shared/components/PhotoOrInitials';
import { colorTokens } from '../../../shared/components/variables';
import { Rider } from '../../types/rideOverview';
import { EditRideDetailsWaypoints, MapDetailsWaypoint, RiderOnRide } from '../../types/rides';

import { DriverPositionDetails } from './DriverPositionDetails/DriverPositionDetails';
import { INITIAL_ZOOM_LEVEL } from './RouteMap.helpers';
import { MapContainer, MarkerDot, TrafficToggleContainer } from './RouteMap.styles';
import { useMapCenter } from './useMapCenter';

interface RouteMapViewProps {
  isFetching: boolean;
  plannedRoute: string;
  orderedWaypointCoordinates: MapDetailsWaypoint[];
  riders: RiderOnRide[];
  waypointsAttributes: EditRideDetailsWaypoints[];
  ridersInCar: Rider[];
  hasAlertsBannner?: boolean;
}

const staticEmptyMarkerSvg =
  '<svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52" fill="none"></svg>';

const Marker: FC<Omit<OverlayViewProps, 'mapPaneName'>> = ({ children, ...rest }) => {
  const getPixelPositionOffset = (width, height) => ({
    x: -(width / 2),
    y: -(height / 2),
  });

  return (
    <OverlayView
      {...rest}
      getPixelPositionOffset={getPixelPositionOffset}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      {children}
    </OverlayView>
  );
};

export const RouteMap: React.FC<RouteMapViewProps> = ({
  isFetching,
  plannedRoute,
  orderedWaypointCoordinates,
  riders,
  waypointsAttributes,
  ridersInCar,
  hasAlertsBannner = false,
}) => {
  const [mapInstance, setMapInstance] = useState<google.maps.Map>();
  const [isTooltipOpen, setIsTooltipOpen] = useState(-1);
  const [showTrafficLayer, setShowTrafficLayer] = useState<boolean>(false);
  const { center } = useMapCenter({ mapInstance, orderedWaypointCoordinates: orderedWaypointCoordinates });

  const routePolyline =
    plannedRoute && !!mapInstance && window.google?.maps?.geometry.encoding.decodePath(plannedRoute);

  return (
    <MapContainer $hasAlertsBannner={hasAlertsBannner}>
      <Maps
        googleMapsApiKey={window.env?.CARE_MAPS_API_KEY}
        handleMapInstance={(newMapInstance) => setMapInstance(newMapInstance)}
        center={center}
        zoom={INITIAL_ZOOM_LEVEL}
        height={'100%'}
        minWidth={'100%'}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: true,
          cameraControlOptions: {
            position: 3,
          },
        }}
      >
        <TrafficToggleContainer>
          <Switch checked={showTrafficLayer} onChange={() => setShowTrafficLayer(!showTrafficLayer)} /> Traffic
        </TrafficToggleContainer>
        <DriverPositionDetails ridersInCar={ridersInCar} />
        {showTrafficLayer && <Maps.TrafficLayer />}
        {routePolyline && (
          <Maps.Polyline
            data-testid="planned-route-polyline"
            options={{
              path: routePolyline,
              geodesic: true,
              strokeColor: colorTokens.primary.light,
              strokeOpacity: 0.7,
              strokeWeight: 4.345,
              zIndex: 1,
            }}
          />
        )}
        {orderedWaypointCoordinates?.map((waypoint, index) => (
          <>
            <Marker key={`${index}-waypoint-marker`} position={waypoint}>
              <MarkerDot onMouseOver={() => setIsTooltipOpen(index)} onMouseOut={() => setIsTooltipOpen(-1)}>
                {waypoint.anchor && (
                  <Svg icon="anchor-rounded" color={colorTokens.primary.main} width="16px" height="18px" />
                )}
                <Text variant="body1" style={{ color: colorTokens.primary.main }}>{`${index + 1}: ${getTypeOfWaypoint(
                  orderedWaypointCoordinates,
                  index
                )}`}</Text>
              </MarkerDot>
            </Marker>

            <Maps.GoogleMarker
              key={`${index}-waypoint-infowindow`}
              position={waypoint}
              icon={{
                url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(staticEmptyMarkerSvg),
              }}
            >
              {isTooltipOpen === index && (
                <Maps.InfoWindow position={waypoint} options={{ pixelOffset: new window.google.maps.Size(40, 50) }}>
                  <div
                    style={{
                      display: 'flex',
                      gap: '2px',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Svg icon="sidebar_riders" color={colorTokens.primary.main} />
                    {waypoint.anchor
                      ? riders.map((rider) => (
                          <PhotoOrInitials
                            pictureUrl={rider?.profilePictureUrl || ''}
                            key={`anchor-riders-${rider?.id}`}
                            fullName={`${rider?.firstName} ${rider?.lastName}`}
                            size={33}
                            fontSize={14}
                          />
                        ))
                      : waypointsAttributes[index]?.passengerIdsStrings?.map((passengerId) => {
                          const riderDetails = riders.find((r) => r.id && r.id.toString() === passengerId);

                          return (
                            <PhotoOrInitials
                              pictureUrl={riderDetails?.profilePictureUrl || ''}
                              key={`non-anchor-riders-${passengerId}`}
                              fullName={`${riderDetails?.firstName} ${riderDetails?.lastName}`}
                              size={33}
                              fontSize={14}
                            />
                          );
                        })}
                  </div>
                </Maps.InfoWindow>
              )}
            </Maps.GoogleMarker>
          </>
        ))}
      </Maps>
      <MapLoader isLoading={isFetching} />
    </MapContainer>
  );
};

function getTypeOfWaypoint(waypoints, index) {
  return index === 0 || (waypoints.slice(-1)[0].anchor && index < waypoints.length - 1) ? 'PU' : 'DO';
}

export const MapLoader = ({ isLoading }) => (
  <Backdrop open={isLoading} transitionDuration={{ appear: 500, enter: 200, exit: 500 }}>
    <Loader />
  </Backdrop>
);
